import React, { useCallback, useEffect, useMemo, useState } from 'react';
import StripeCheckout from 'react-stripe-checkout';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import api from '../../Utils/Axios';
import { setHospitalInfo } from '../../store/hospitalSlice';
import './PricingPlan.css';

const stripePublishableKey =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_STRIPE_PUBLISH_KEY ||
  'pk_test_51OGbliKue2i3LW4Npe6oAwbcfHyFSSUdcRPKRejkqG5z1WggCgT2MnaW3ayQaPV6gnVugP7w3C5crbJyug9weV1e00CESUwd87';

const PricingPlan = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hospitalInfo = useSelector((state) => state.hospital.hospitalInfo);

  const [pricing, setPricing] = useState({
    trialDays: 30,
    pricePerDayUSD: 2.5,
    pricePerMonthUSD: 75,
  });
  const [trial, setTrial] = useState({ isTrialActive: false, remainingDays: 0 });
  const [requiresPlanSelection, setRequiresPlanSelection] = useState(true);
  const [billing, setBilling] = useState({
    hasCardOnFile: false,
    cardBrand: '',
    cardLast4: '',
    cardExpMonth: null,
    cardExpYear: null,
  });
  const [submittingCard, setSubmittingCard] = useState(false);
  const [submittingPremium, setSubmittingPremium] = useState(false);

  const trialDays = Number(pricing?.trialDays || 30);
  const perDayPrice = Number(pricing?.pricePerDayUSD || 2.5);
  const perMonthPrice = Number(pricing?.pricePerMonthUSD || 75);
  const hasStripeKey = Boolean(stripePublishableKey);

  const billingLabel = useMemo(() => {
    if (!billing?.hasCardOnFile) return 'No card on file';
    const exp =
      billing?.cardExpMonth && billing?.cardExpYear
        ? ` (Exp ${billing.cardExpMonth}/${billing.cardExpYear})`
        : '';
    return `${billing.cardBrand || 'Card'} **** ${billing.cardLast4 || ''}${exp}`;
  }, [billing]);

  const loadPricing = useCallback(async () => {
    try {
      const response = await api.get('/api/hospital/posting-config');
      setPricing(
        response.data?.pricing || { trialDays: 30, pricePerDayUSD: 2.5, pricePerMonthUSD: 75 }
      );
      setTrial(response.data?.trial || { isTrialActive: false, remainingDays: 0 });
      setRequiresPlanSelection(Boolean(response.data?.requiresPlanSelection));
      setBilling(
        response.data?.billing || {
          hasCardOnFile: false,
          cardBrand: '',
          cardLast4: '',
          cardExpMonth: null,
          cardExpYear: null,
        }
      );
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error('Please login with your hospital account first.');
        navigate('/login');
        return;
      }
      toast.error('Failed to load pricing settings');
    }
  }, [navigate]);

  useEffect(() => {
    if (!hospitalInfo?.authToken || hospitalInfo?.userType !== 'hospital') {
      navigate('/login');
      return;
    }
    loadPricing();
  }, [hospitalInfo?.authToken, hospitalInfo?.userType, loadPricing, navigate]);

  const onCardToken = async (token) => {
    setSubmittingCard(true);
    try {
      const response = await api.post('/api/hospital/select-plan', {
        planType: 'free',
        token,
      });
      setTrial(response.data?.trial || trial);
      setBilling(response.data?.billing || billing);
      setRequiresPlanSelection(false);
      toast.success('Card saved and free trial activated. Please log in to continue.');
      dispatch(setHospitalInfo(null));
      navigate('/login');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to save card and activate plan');
    } finally {
      setSubmittingCard(false);
    }
  };

  const onPremiumSelect = async (token, billingCycle) => {
    setSubmittingPremium(true);
    try {
      const response = await api.post('/api/hospital/select-plan', {
        planType: 'premium',
        token,
        billingCycle,
      });
      setTrial(response.data?.trial || trial);
      setBilling(response.data?.billing || billing);
      setRequiresPlanSelection(false);
      toast.success('Premium plan activated. Please log in to continue.');
      dispatch(setHospitalInfo(null));
      navigate('/login');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to activate premium plan');
    } finally {
      setSubmittingPremium(false);
    }
  };

  return (
    <section className="hpwp-pricing-page pt-120 mb-120">
      <div className="container">
        <div className="hpwp-pricing-top text-center">
          <h2>Select Your Hospital Plan</h2>
          <p>Choose a plan to continue. Free plan requires card details to activate your trial.</p>
        </div>

        <div className="row g-4 align-items-stretch">
          <div className="col-lg-4">
            <div className="hpwp-plan-card active">
              <div className="hpwp-plan-head">
                <h4>Free Plan</h4>
                <span className="hpwp-plan-tag">Recommended to Start</span>
              </div>
              <h3>{trialDays} Days Free</h3>
              <p className="hpwp-plan-sub">
                After trial, switch to a Premium plan to continue posting.
              </p>
              <ul>
                <li>Full access during free trial</li>
                <li>Post healthcare jobs nationwide</li>
                <li>Card required for activation</li>
                <li>Upgrade to Premium when trial ends</li>
              </ul>

              <p className="hpwp-plan-note">
                Billing: <strong>{billingLabel}</strong>
              </p>
              <p className="hpwp-plan-note">
                Trial:{' '}
                <strong>
                  {requiresPlanSelection ? 'Not started' : trial?.isTrialActive ? `${trial?.remainingDays || 0} day(s) left` : 'Expired'}
                </strong>
              </p>

              <div className="hpwp-plan-actions">
                {!hasStripeKey && <p className="hpwp-inline-error">Stripe key is missing in frontend environment.</p>}
                <StripeCheckout
                  stripeKey={stripePublishableKey}
                  token={onCardToken}
                  amount={0}
                  currency="USD"
                  name="HPWP Free Plan"
                  description={`Activate ${trialDays}-day free trial`}
                  panelLabel="Save Card"
                  billingAddress
                  zipCode
                >
                  <button
                    className="hpwp-plan-button hpwp-plan-button-free w-100"
                    type="button"
                    disabled={submittingCard || !hasStripeKey}
                  >
                    {submittingCard ? 'Saving Card...' : 'Select Free Plan'}
                  </button>
                </StripeCheckout>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="hpwp-plan-card premium">
              <div className="hpwp-plan-head">
                <h4>Premium Daily</h4>
                <span className="hpwp-plan-tag">Per Day</span>
              </div>
              <h3>${perDayPrice} / day</h3>
              <p className="hpwp-plan-sub">Unlimited postings billed once per day of activity.</p>
              <ul>
                <li>Bulk posting workflow</li>
                <li>Priority support lane</li>
                <li>Custom invoicing options</li>
                <li>Dedicated account manager</li>
              </ul>
              <div className="hpwp-plan-actions">
                <StripeCheckout
                  stripeKey={stripePublishableKey}
                  token={(token) => onPremiumSelect(token, 'daily')}
                  amount={0}
                  currency="USD"
                  name="HPWP Premium Daily"
                  description={`Save card to enable $${perDayPrice} per day`}
                  panelLabel="Save Card"
                  billingAddress
                  zipCode
                >
                  <button
                    className="hpwp-plan-button hpwp-plan-button-premium w-100"
                    type="button"
                    disabled={submittingPremium || !hasStripeKey}
                  >
                    {submittingPremium ? 'Activating...' : 'Select Premium Daily'}
                  </button>
                </StripeCheckout>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="hpwp-plan-card premium">
              <div className="hpwp-plan-head">
                <h4>Premium Monthly</h4>
                <span className="hpwp-plan-tag">Per Month</span>
              </div>
              <h3>${perMonthPrice} / month</h3>
              <p className="hpwp-plan-sub">Best value for continuous hiring teams.</p>
              <ul>
                <li>Unlimited postings all month</li>
                <li>Priority support lane</li>
                <li>Custom invoicing options</li>
                <li>Dedicated account manager</li>
              </ul>
              <div className="hpwp-plan-actions">
                <StripeCheckout
                  stripeKey={stripePublishableKey}
                  token={(token) => onPremiumSelect(token, 'monthly')}
                  amount={0}
                  currency="USD"
                  name="HPWP Premium Monthly"
                  description={`Save card to enable $${perMonthPrice} per month`}
                  panelLabel="Save Card"
                  billingAddress
                  zipCode
                >
                  <button
                    className="hpwp-plan-button hpwp-plan-button-premium w-100"
                    type="button"
                    disabled={submittingPremium || !hasStripeKey}
                  >
                    {submittingPremium ? 'Activating...' : 'Select Premium Monthly'}
                  </button>
                </StripeCheckout>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingPlan;
